// AuditLogFeed.jsx
import * as React from "react";
import PropTypes from "prop-types";
import {
    Box,
    List,
    ListItem,
    ListItemAvatar,
    Avatar,
    ListItemText,
    Stack,
    Chip,
    Divider,
    Tooltip,
    Typography
} from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {ChangeRow, ChipValue} from "./ChipValue";

/**
 * @param {Object} props
 * @param {Array<{
 *   tableName: string,
 *   columnName: string,
 *   oldValue: string|null,
 *   newValue: string|null,
 *   operation: "CREATE"|"UPDATE"|"DELETE",
 *   rowId: string,
 *   occurredAt: string, // ISO instant
 *   user: { name: string }
 * }>} props.logs
 */
export default function AuditLogFeed({ logs = [] }) {
    const fmt = new Intl.DateTimeFormat("ru-RU", {
        dateStyle: "medium",
        timeStyle: "short"
    });

    const pretty = (s) =>
        (s ?? "")
            .toString()
            .replace(/_/g, " ")
            .toLowerCase()
            .replace(/(^|\s)\S/g, (c) => c.toUpperCase());

    const opIcon = (op) => {
        switch (op) {
            case "CREATE":
                return <AddIcon fontSize="small" />;
            case "DELETE":
                return <DeleteIcon fontSize="small" />;
            default:
                return <EditIcon fontSize="small" />;
        }
    };

    const opColor = (op) => {
        switch (op) {
            case "CREATE":
                return "success";
            case "DELETE":
                return "error";
            default:
                return "warning";
        }
    };

    if (!logs.length) {
        return (
            <Box sx={{ opacity: 0.7, fontStyle: "italic", p: 1 }}>
                Нет событий аудита.
            </Box>
        );
    }

    return (
        <List disablePadding>
            {logs.map((item, idx) => {
                const {
                    tableName,
                    columnName,
                    oldValue,
                    newValue,
                    operation,
                    rowId,
                    occurredAt,
                    user
                } = item;

                const primaryLine = (
                    <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={1}>
                        <Typography component="span" sx={{ fontWeight: 600 }}>
                            {user?.name || "Неизвестный пользователь"}
                        </Typography>
                        <Chip
                            size="small"
                            icon={opIcon(operation)}
                            label={pretty(operation)}
                            color={opColor(operation)}
                            variant="filled"
                            sx={{ height: 22 }}
                        />
                        <Typography component="span">
                            в <b>{pretty(tableName)}</b>
                            {columnName ? (
                                <>
                                    {" "}
                                    поле <b>{pretty(columnName)}</b>
                                </>
                            ) : null}{" "}
                            (id: <b>{rowId}</b>)
                        </Typography>
                    </Stack>
                );

                const valueLine =
                    operation === "DELETE" ? (
                        // Удаление — показываем, что было
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ mt: 0.5, flexWrap: "wrap" }}
                        >
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                Было:
                            </Typography>
                            <Chip
                                variant="outlined"
                                size="small"
                                label={stringify(oldValue)}
                            />
                        </Stack>
                    ) : operation === "CREATE" ? (
                        // Создание — показываем, что стало
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ mt: 0.5, flexWrap: "wrap" }}
                        >
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                Стало:
                            </Typography>
                            <Chip
                                variant="outlined"
                                size="small"
                                label={stringify(newValue)}
                            />
                        </Stack>
                    ) : (
                        // Обновление — стрелка от старого к новому
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ mt: 0.5, flexWrap: "wrap" }}
                        >
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                Было:
                            </Typography>

                            <ChangeRow oldValue={stringify(oldValue)} newValue={stringify(newValue)}/>
                            {/*<ChipValue*/}
                            {/*    variant="outlined"*/}
                            {/*    size="small"*/}
                            {/*    label={}*/}
                            {/*/>*/}
                            {/*<ArrowRightAltIcon fontSize="small" />*/}
                            {/*<Typography variant="body2" sx={{ opacity: 0.8 }}>*/}
                            {/*    Стало:*/}
                            {/*</Typography>*/}
                            {/*<ChipValue variant="outlined" size="small" label={stringify(stringify(oldValue))} />*/}
                        </Stack>
                    );

                const secondaryLine = (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ mt: 0.5, flexWrap: "wrap" }}
                    >
                        <Tooltip title="Время события">
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                {fmt.format(new Date(occurredAt))}
                            </Typography>
                        </Tooltip>
                        <Typography variant="caption" sx={{ opacity: 0.5 }}>
                            •
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                            {pretty(tableName)}
                            {columnName ? ` · ${pretty(columnName)}` : ""}
                        </Typography>
                    </Stack>
                );

                return (
                    <React.Fragment key={`${occurredAt}-${rowId}-${idx}`}>
                        <ListItem
                            alignItems="flex-start"
                            sx={{
                                px: 0,
                                py: 1.25
                            }}
                        >
                            <ListItemAvatar>
                                <Avatar
                                    sx={{ width: 36, height: 36, fontSize: 14 }}
                                    alt={user?.name || "User"}
                                >
                                    {initials(user?.name)}
                                </Avatar>
                            </ListItemAvatar>

                            <ListItemText
                                primary={primaryLine}
                                secondary={
                                    <Box sx={{ mt: 0.25 }}>
                                        {valueLine}
                                        {secondaryLine}
                                    </Box>
                                }
                            />
                        </ListItem>
                        {idx < logs.length - 1 && <Divider sx={{ my: 0.5 }} />}
                    </React.Fragment>
                );
            })}
        </List>
    );
}

AuditLogFeed.propTypes = {
    logs: PropTypes.arrayOf(
        PropTypes.shape({
            tableName: PropTypes.string.isRequired,
            columnName: PropTypes.string,
            oldValue: PropTypes.any,
            newValue: PropTypes.any,
            operation: PropTypes.oneOf(["CREATE", "UPDATE", "DELETE"]).isRequired,
            rowId: PropTypes.string.isRequired,
            occurredAt: PropTypes.string.isRequired,
            user: PropTypes.shape({ name: PropTypes.string })
        })
    )
};

// ===== helpers =====
function initials(name = "") {
    const parts = name.trim().split(/\s+/);
    if (!parts.length) return "U";
    const [a, b] = parts;
    return (a?.[0] || "") + (b?.[0] || "");
}

function stringify(v) {
    if (v === null || v === undefined) return "—";
    if (typeof v === "object") {
        try {
            return JSON.stringify(v);
        } catch {
            return String(v);
        }
    }
    return String(v);
}
